import {
  Alert,
  Button,
  Item,
  ItemGroup,
  ItemHeader,
  ItemTitle,
  ScrollArea,
  Sentinel,
  Skeleton,
  Spinner,
  TextWithDescription,
} from '@ibsys/kit';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployeesInfinite } from '@/entities/employee';
import { useGuardedInfiniteScroll } from '@/shared/hooks';
import { cn, getFullName } from '@/shared/lib';

type Props = {
  selectedId?: string;
  searchQuery?: string;
  className?: string;
  onSelect: (id: number) => void;
};

export const EmployeeList: FC<Props> = ({ selectedId, searchQuery, className, onSelect }) => {
  const { t } = useTranslation(['employees', 'common']);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useEmployeesInfinite(searchQuery);
  const viewportRef = useRef<HTMLDivElement>(null);
  const { sentinelRef, isAutoLoadStopped, resumeAutoLoad } = useGuardedInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    pagesCount: data?.pages.length ?? 0,
    resetKey: searchQuery,
    threshold: 500,
    root: viewportRef,
  });

  const employees = useMemo(() => {
    const list = data?.pages.flatMap((p) => p.employees) ?? [];
    return list.sort((a, b) => {
      const fioA = getFullName(a.firstName, a.lastName, a.middleName);
      const fioB = getFullName(b.firstName, b.lastName, b.middleName);
      return fioA.localeCompare(fioB, 'ru');
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (!employees.length) {
    return <Alert className="text-muted-foreground p-4" title={t('employees:listEmpty')} />;
  }

  return (
    <ScrollArea className={cn('pb-2', className)} viewPortProps={{ ref: viewportRef }}>
      <ItemGroup className="flex flex-col gap-4">
        {employees.map((employee) => {
          const isActive = selectedId === String(employee.id);
          const fio = getFullName(employee.firstName, employee.lastName, employee.middleName);

          return (
            <Item
              key={employee.id}
              variant={isActive ? 'muted' : 'outline'}
              className={cn(
                'hover:bg-muted hover:text-muted-foreground cursor-pointer',
                isActive && 'bg-accent text-accent-foreground',
              )}
              onClick={() => onSelect(employee.id)}
            >
              <ItemHeader>
                <ItemTitle>
                  <TextWithDescription
                    text={<span>{fio}</span>}
                    description={
                      employee.externalId ? (
                        <span className="text-sm">
                          {t('employees:externalId')}: {employee.externalId}
                        </span>
                      ) : undefined
                    }
                  />
                </ItemTitle>
              </ItemHeader>
            </Item>
          );
        })}
        <Sentinel ref={sentinelRef} style={{ width: '100%' }}>
          {isFetchingNextPage && (
            <div className="w-full flex justify-center py-6">
              <Spinner className="size-8" />
            </div>
          )}
          {isAutoLoadStopped && (
            <div className="w-full flex flex-col items-center gap-2 py-6">
              <Alert type="warning" title={t('common:loadMoreFailed')} />
              <Button variant="secondary" size="sm" onClick={resumeAutoLoad}>
                {t('common:retry')}
              </Button>
            </div>
          )}
        </Sentinel>
      </ItemGroup>
    </ScrollArea>
  );
};
